import { HEN_BREEDS } from '../data/breeds';
import { ROOSTER_BREEDS } from '../data/roosters';
import { ROOSTER_NAMES } from '../data/roosterNames';
import { UPGRADES_BY_ID } from '../data/upgrades';
import { GATHER_BY_ID, INFRA_BY_ID } from '../data/farm';
import { BOXES_PER_FLOCK, LAYING_PHASE_SEC, MAX_FLOCKS, VOUCHER_MAX } from '../data/balance';
import type { BreedId } from '../data/types';
import { clutchProduct, consumeClutch, addBuff } from './buffs';
import { recordComboClick } from './combo';
import {
  clickGather, crowStats, earn, henCost, henStats, infraCost, nestBoxCost, nestUpgradeCost, roosterCost,
  roosterSlots, spend, upgradeCost,
} from './economy';
import { reqsMet } from './requirements';
import { rand, randInt } from './rng';
import { eggRoll } from './specials';
import { emit, findFlock, logActivity, makeBox, makeFlock, makeHen, touch } from './state';
import type { Flock, GameState, Hen, SimCtx } from './types';

export type ActionResult = { ok: true; amount: number; detail?: string } | { ok: false; reason: string };

const ok = (amount = 0, detail?: string): ActionResult => (detail === undefined ? { ok: true, amount } : { ok: true, amount, detail });
const fail = (reason: string): ActionResult => ({ ok: false, reason });

const money = (n: number) => `$${Math.floor(n).toLocaleString('en-US')}`;

// ── Collection ────────────────────────────────────────────────────────

export function recordEggsCollected(s: GameState, eggs: number): void {
  s.eggsCollected += eggs;
  const sec = Math.floor(s.time / 1000);
  const st = s.stats;
  if (st.eggBuckets.length === 0 || sec - st.eggBucketSec > 300) {
    st.eggBuckets = [];
    st.eggBucketSec = sec;
  }
  // Advance the rolling 300-second window.
  while (st.eggBucketSec < sec) {
    st.eggBuckets.push(0);
    st.eggBucketSec++;
    if (st.eggBuckets.length > 300) st.eggBuckets.shift();
  }
  if (st.eggBuckets.length === 0) st.eggBuckets.push(0);
  st.eggBuckets[st.eggBuckets.length - 1]! += eggs;
}

/** Click a nest: collect up to the click-gather count of eggs. */
export function collectNest(s: GameState, ctx: SimCtx, flockId: number, boxIndex: number): ActionResult {
  const flock = findFlock(s, flockId);
  const box = flock?.boxes[boxIndex];
  if (!flock || !box?.hen) return fail('No hen here');
  if (box.eggs <= 0) return fail('empty');
  recordComboClick(s, s.time);
  const take = Math.min(box.eggs, clickGather(s));
  const st = henStats(s, ctx, flock, box.hen);
  let total = 0;
  let procLabel: string | null = null;
  for (let i = 0; i < take; i++) {
    const roll = eggRoll(box.hen, rand(s));
    if (roll.label) procLabel = roll.label;
    total += st.eggValue * st.clickMult * clutchProduct(s.clutch) * roll.mult;
    consumeClutch(s.clutch, 1);
  }
  box.eggs -= take;
  if (box.eggs <= 0) box.oldestEggAt = null;
  earn(s, total);
  s.stats.clicks++;
  recordEggsCollected(s, take);
  return ok(total, procLabel ?? `${take} egg${take === 1 ? '' : 's'}`);
}

// ── Nest boxes & hens ─────────────────────────────────────────────────

export function buyNestBox(s: GameState, flockId: number): ActionResult {
  const flock = findFlock(s, flockId);
  if (!flock) return fail('No such flock');
  if (flock.boxes.length >= BOXES_PER_FLOCK) return fail('This flock is full');
  const cost = nestBoxCost(s, flockId);
  if (!spend(s, cost)) return fail('Not enough money');
  flock.boxes.push(makeBox());
  logActivity(s, 'purchase', 'Nesting box', `${money(cost)} — ${flock.boxes.length}/${BOXES_PER_FLOCK} in ${flock.name}`);
  touch(s);
  return ok(cost);
}

export function layingPhaseMs(breed: BreedId, flock: Flock): number {
  const b = HEN_BREEDS[breed];
  const sec = LAYING_PHASE_SEC[b.broodiness] ?? 360;
  const mod = flock.rooster ? ROOSTER_BREEDS[flock.rooster.breed].broodyMod : 1;
  return sec * 1000 * mod;
}

export function placeHen(s: GameState, ctx: SimCtx, flockId: number, boxIndex: number, breed: BreedId): ActionResult {
  const flock = findFlock(s, flockId);
  const box = flock?.boxes[boxIndex];
  if (!flock || !box) return fail('No such nest');
  if (box.hen) return fail('Nest is occupied');
  if (!s.unlockedBreeds.includes(breed)) return fail('Breed is locked');
  const cost = henCost(s, breed, flockId);
  if (!spend(s, cost)) return fail('Not enough money');
  const hen = makeHen(breed, ctx.wall, randInt(s, 0, 2));
  if (HEN_BREEDS[breed].broodyProne) hen.layingUntil = s.time + layingPhaseMs(breed, flock);
  box.hen = hen;
  box.eggs = 0;
  box.oldestEggAt = null;
  s.newBreeds = s.newBreeds.filter((b) => b !== breed);
  logActivity(s, 'purchase', 'Hen', `${HEN_BREEDS[breed].name} — ${money(cost)}`);
  touch(s);
  return ok(cost);
}

/** Buy a box (if needed) and place a hen in the first empty nest of a flock. */
export function buyHen(s: GameState, ctx: SimCtx, flockId: number, breed: BreedId): ActionResult {
  const flock = findFlock(s, flockId);
  if (!flock) return fail('No such flock');
  const empty = flock.boxes.findIndex((b) => !b.hen);
  if (empty >= 0) return placeHen(s, ctx, flockId, empty, breed);
  return fail('No empty nest — buy a nesting box first');
}

export function releaseHen(s: GameState, flockId: number, boxIndex: number): ActionResult {
  const flock = findFlock(s, flockId);
  const box = flock?.boxes[boxIndex];
  if (!flock || !box?.hen) return fail('No hen here');
  const name = HEN_BREEDS[box.hen.breed].name;
  box.hen = null;
  box.eggs = 0;
  box.oldestEggAt = null;
  s.events = s.events.filter((e) => !(e.kind === 'broody' && e.flockId === flockId && e.boxIndex === boxIndex));
  logActivity(s, 'purchase', 'Released', `${name} went to live on a nicer farm`);
  touch(s);
  return ok();
}

export function upgradeNest(s: GameState, flockId: number, boxIndex: number): ActionResult {
  const box = findFlock(s, flockId)?.boxes[boxIndex];
  if (!box) return fail('No such nest');
  const cost = nestUpgradeCost(box);
  if (cost === null) return fail('Nest is at max capacity');
  if (!spend(s, cost)) return fail('Not enough money');
  box.capacityLevel++;
  return ok(cost);
}

/** Buy the cheapest nest-capacity upgrades in a flock until money runs out. */
export function upgradeNestsMax(s: GameState, flockId: number): ActionResult {
  const flock = findFlock(s, flockId);
  if (!flock) return fail('No such flock');
  let count = 0;
  let spent = 0;
  for (;;) {
    let best: { i: number; cost: number } | null = null;
    flock.boxes.forEach((b, i) => {
      const c = nestUpgradeCost(b);
      if (c !== null && (!best || c < best.cost)) best = { i, cost: c };
    });
    const pick = best as { i: number; cost: number } | null;
    if (!pick || s.money < pick.cost) break;
    spend(s, pick.cost);
    flock.boxes[pick.i]!.capacityLevel++;
    count++;
    spent += pick.cost;
  }
  if (count === 0) return fail('Nothing affordable');
  logActivity(s, 'purchase', 'Nest capacity', `${count} upgrades — ${money(spent)}`);
  return ok(spent, `${count} nests upgraded`);
}

// ── Upgrades, gathering, infrastructure ───────────────────────────────

export function buyUpgrade(s: GameState, id: string, flockId?: number): ActionResult {
  const def = UPGRADES_BY_ID.get(id);
  if (!def) return fail('Unknown upgrade');
  if (!reqsMet(s, def.requires)) return fail('Requirements not met');
  if (def.category === 'coop') {
    const flock = flockId === undefined ? undefined : findFlock(s, flockId);
    if (!flock) return fail('Pick a flock');
    if (flock.coop.includes(id)) return fail('Already owned');
    const cost = upgradeCost(s, def, flock.id);
    if (!spend(s, cost)) return fail('Not enough money');
    flock.coop.push(id);
    logActivity(s, 'purchase', def.name, `${flock.name} — ${money(cost)}`);
    touch(s);
    return ok(cost);
  }
  if (s.upgrades.includes(id)) return fail('Already owned');
  const cost = upgradeCost(s, def);
  if (!spend(s, cost)) return fail('Not enough money');
  s.upgrades.push(id);
  logActivity(s, 'purchase', def.name, money(cost));
  touch(s);
  return ok(cost);
}

export function buyGather(s: GameState, id: string): ActionResult {
  const def = GATHER_BY_ID.get(id);
  if (!def) return fail('Unknown gathering tier');
  if (def.postWin && !s.won) return fail('Unlocks after you win');
  const owned = s.gather[id] ?? 0;
  if (owned >= def.maxOwned) return fail('Already owned');
  if (!spend(s, def.cost)) return fail('Not enough money');
  s.gather[id] = owned + 1;
  logActivity(s, 'purchase', def.name, money(def.cost));
  touch(s);
  return ok(def.cost);
}

export function buyInfra(s: GameState, id: string): ActionResult {
  const def = INFRA_BY_ID.get(id);
  if (!def) return fail('Unknown building');
  if (!reqsMet(s, def.requires)) return fail('Requirements not met');
  const owned = s.infra[def.id] ?? 0;
  if (owned >= def.maxOwned) return fail('At max');
  const cost = infraCost(s, def);
  if (!spend(s, cost)) return fail('Not enough money');
  s.infra[def.id] = owned + 1;
  logActivity(s, 'purchase', def.name, `#${owned + 1} — ${money(cost)}`);
  touch(s);
  return ok(cost);
}

// ── Roosters ──────────────────────────────────────────────────────────

export function generateRoosterName(s: GameState): string {
  const used = new Set(s.usedRoosterNames);
  let pool = ROOSTER_NAMES.filter((n) => !used.has(n));
  if (pool.length === 0) {
    s.usedRoosterNames = [];
    pool = [...ROOSTER_NAMES];
  }
  const name = pool[Math.floor(rand(s) * pool.length)]!;
  s.usedRoosterNames.push(name);
  return name;
}

/** Which existing flock a newly bought rooster joins (first rooster-less flock), or null for a new flock. */
export function roosterTargetFlock(s: GameState): Flock | null {
  return s.flocks.find((f) => !f.rooster) ?? null;
}

export function buyRooster(s: GameState, ctx: SimCtx, breed: BreedId): ActionResult {
  const slots = roosterSlots(s);
  if (slots.open <= 0) return fail(slots.nextAtHens ? `Reach ${slots.nextAtHens} hens to unlock the next rooster` : 'All rooster slots are filled');
  if (!s.unlockedBreeds.includes(breed)) return fail(`Unlock ${HEN_BREEDS[breed].name} hens first`);
  const cost = roosterCost(s);
  if (!Number.isFinite(cost)) return fail('You have every rooster you can keep');
  if (!spend(s, cost)) return fail('Not enough money');
  let flock = roosterTargetFlock(s);
  if (!flock) {
    if (s.flocks.length >= MAX_FLOCKS) return fail('No room for another flock');
    const nextFlockId = Math.max(...s.flocks.map((f) => f.id)) + 1;
    flock = makeFlock(nextFlockId);
    s.flocks.push(flock);
  }
  const usedVouchers = Math.min(VOUCHER_MAX, s.vouchers);
  s.vouchers -= usedVouchers;
  const name = generateRoosterName(s);
  flock.rooster = { breed, variant: randInt(s, 0, 2), name, placedWall: ctx.wall, crowReadyAt: s.time };
  if (!flock.nameIsCustom) flock.name = `${name}'s flock`;
  if (!s.roosterBreedsEver.includes(breed)) s.roosterBreedsEver.push(breed);
  s.stats.roosterWalls.push(ctx.wall);
  // Re-time broody-prone laying phases for the new rooster's broodyMod.
  for (const b of flock.boxes) {
    if (b.hen && HEN_BREEDS[b.hen.breed].broodyProne && b.hen.broodySince === null) {
      b.hen.layingUntil = s.time + layingPhaseMs(b.hen.breed, flock);
    }
  }
  logActivity(s, 'purchase', 'Rooster', `${name} the ${ROOSTER_BREEDS[breed].name} — ${money(cost)}${usedVouchers ? ` (${usedVouchers} cockerel voucher${usedVouchers > 1 ? 's' : ''})` : ''}`);
  emit(s, { kind: 'toast', text: `${name} the ${ROOSTER_BREEDS[breed].name} now rules ${flock.name}.`, tone: 'good' });
  touch(s);
  return ok(cost);
}

export function releaseRooster(s: GameState, flockId: number): ActionResult {
  const flock = findFlock(s, flockId);
  if (!flock?.rooster) return fail('No rooster here');
  const name = flock.rooster.name;
  flock.rooster = null;
  flock.crowBuffs = [];
  logActivity(s, 'purchase', 'Released', `${name} retired to a hobby farm`);
  touch(s);
  return ok();
}

function sanitizeName(raw: string, max: number): string {
  return String(raw ?? '').replace(/[\u0000-\u001f\u007f<>]/g, '').trim().slice(0, max);
}

export function renameRooster(s: GameState, flockId: number, name: string): ActionResult {
  const flock = findFlock(s, flockId);
  if (!flock?.rooster) return fail('No rooster here');
  const clean = sanitizeName(name, 24);
  if (!clean) return fail('Name is empty');
  flock.rooster.name = clean;
  if (!flock.nameIsCustom) flock.name = `${clean}'s flock`;
  return ok();
}

export function renameFlock(s: GameState, flockId: number, name: string): ActionResult {
  const flock = findFlock(s, flockId);
  if (!flock) return fail('No such flock');
  const clean = sanitizeName(name, 32);
  if (!clean) return fail('Name is empty');
  flock.name = clean;
  flock.nameIsCustom = true;
  return ok();
}

export function setFarmName(s: GameState, name: string): ActionResult {
  const clean = sanitizeName(name, 30);
  s.farmName = clean || 'My Farm';
  return ok();
}

export function crow(s: GameState, ctx: SimCtx, flockId: number): ActionResult {
  const flock = findFlock(s, flockId);
  const rooster = flock?.rooster;
  if (!flock || !rooster) return fail('No rooster');
  if (s.time < rooster.crowReadyAt) return fail('Still catching his breath');
  const c = crowStats(s, ctx, rooster.breed);
  addBuff(flock.crowBuffs, c.power, c.durationMs, s.time, `crow:${rooster.breed}`);
  rooster.crowReadyAt = s.time + c.cooldownMs;
  s.stats.crows++;
  return ok(0, `×${c.power.toFixed(1)} laying for ${Math.round(c.durationMs / 1000)}s`);
}

export function crowReadyIn(s: GameState, flock: Flock): number {
  return flock.rooster ? Math.max(0, flock.rooster.crowReadyAt - s.time) : Infinity;
}

export function henAt(s: GameState, flockId: number, boxIndex: number): Hen | null {
  return findFlock(s, flockId)?.boxes[boxIndex]?.hen ?? null;
}
